import { useEffect, useRef, useState, useMemo } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import { useTimesheetsByFilter } from "presentation/hooks/use-query-timesheet";
import { Timesheet } from "domain/entities/timesheet";
import { TimesheetInputFilter } from "domain/models/timesheet-input-filter";
import TimesheetSkeleton from "./Skeleton";
import TimesheetCard from "./components/TimesheetCard";
import { useTripsheetsByFilter } from "presentation/hooks/use-query-tripsheet";
import { TripsheetInputFilter } from "domain/models/tripsheet-input-filter";
import usePermissionCheck from "presentation/hooks/use-check-permissions";
import MobileFilterTabs from "presentation/mobile/components/MobileFilterTabs";
import { useStatusFilter } from "presentation/mobile/hooks/useStatusFilter";
import { ALL_STATUSES_KEY, TIMESHEET_STATUS_FILTERS } from "presentation/mobile/utils/status-filters.util";
import MobileAlertBanner from "presentation/mobile/components/MobileAlertBanner";
import MobileToast from "presentation/mobile/components/MobileToast";

const FUNCTION = "timesheets";
const REQUIRE_FUNCTION = "timesheets";

// Fora do componente para ser estável (o useStatusFilter usa-o nas dependências)
const getTimesheetStatus = (timesheet: Timesheet) => timesheet.status;

const Timesheets = () => {
    const { isLoading: isCheckingPermissions } = usePermissionCheck({
        requiredFunction: REQUIRE_FUNCTION,
    });
    const navigationState = useNavigationStore();
    const { userEmployee } = useAuthStore();
    const employeeId = userEmployee?.id;
    const toast = useRef<Toast>(null);

    const [localTimesheets, setLocalTimesheets] = useState<Timesheet[]>([]);
    const [expandedTimesheetId, setExpandedTimesheetId] = useState<string | null>(null);

    const filter: TimesheetInputFilter = {
        employeeId: employeeId ?? "",
        limit: 100,
        sortBy: "year",
        sortOrder: "desc",
    };

    const {
        data: timesheetData,
        isLoading,
        isRefetching,
    } = useTimesheetsByFilter(filter);

    const { data: tripsheetsData } = useTripsheetsByFilter({
        employeeId: employeeId ?? "",
        status: "Assinado 1",
    } as TripsheetInputFilter);

    const hasPendingKm = (tripsheetsData?.data?.length ?? 0) > 0;

    useEffect(() => {
        if (navigationState.currentRoute !== FUNCTION) {
            navigationState.setCurrentRoute(FUNCTION);
        }
    }, [navigationState]);

    useEffect(() => {
        if (!timesheetData?.data || isLoading) return;
        setLocalTimesheets(timesheetData.data);
    }, [timesheetData?.data, isLoading]);

    // Ordenação decrescente: Ano desc, Mês desc, Versão desc
    const sortedTimesheets = useMemo(() => {
        return [...localTimesheets].sort((a, b) => {
            if (b.year !== a.year) return b.year - a.year;
            if (b.month !== a.month) return b.month - a.month;
            return (b.version ?? 0) - (a.version ?? 0);
        });
    }, [localTimesheets]);

    // #900: abas por estado, contadores e filtragem partilhados com as outras listas mobile
    const {
        activeTab,
        setActiveTab,
        filterTabs,
        filteredItems: filteredTimesheets,
    } = useStatusFilter(sortedTimesheets, TIMESHEET_STATUS_FILTERS, getTimesheetStatus);

    if (isLoading || isRefetching || isCheckingPermissions) {
        return <TimesheetSkeleton />;
    }

    return (
        <div className="flex flex-column pt-2 px-3 gap-2 pb-8 align-items-center">
            {/* Filtros rápidos reutilizáveis */}
            <MobileFilterTabs
                tabs={filterTabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                scrollable
            />

            {/* Cabeçalho da secção */}
            <div className="w-full flex justify-content-between align-items-center pt-1 px-1">
                <span
                    className="font-semibold text-xs"
                    style={{ color: "#64748B" }}
                >
                    Os meus mapas de horas
                </span>
                <span className="text-xs" style={{ color: "#94A3B8" }}>
                    {filteredTimesheets.length} {filteredTimesheets.length === 1 ? "registo" : "registos"}
                </span>
            </div>

            {/* Alerta de KM Pendentes */}
            {hasPendingKm && (
                <MobileAlertBanner severity="warning" className="w-full">
                    Existe(m) mapa(s) de quilómetros pendente(s) de assinatura. Apenas após os assinar, poderá editar e submeter o seu mapa de horas.
                </MobileAlertBanner>
            )}

            {/* Lista de Mapas Filtrados com Expansão */}
            <div className="w-full flex flex-column justify-content-center gap-3">
                {filteredTimesheets.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-filter-slash text-4xl" style={{ color: "#CBD5E1" }} />
                        <span className="text-sm font-medium" style={{ color: "#64748B" }}>
                            {activeTab !== ALL_STATUSES_KEY
                                ? "Nenhum mapa de horas com este estado."
                                : "Sem mapas de horas registados."}
                        </span>
                        {activeTab !== ALL_STATUSES_KEY && (
                            <Button
                                label="Ver todos os mapas"
                                text
                                size="small"
                                className="text-xs font-semibold mt-1"
                                onClick={() => setActiveTab(ALL_STATUSES_KEY)}
                                style={{ color: "#0084D1" }}
                            />
                        )}
                    </div>
                ) : (
                    filteredTimesheets.map((timesheet) => (
                        <TimesheetCard
                            key={timesheet.id}
                            timesheet={timesheet}
                            isExpanded={expandedTimesheetId === timesheet.id}
                            onToggle={() =>
                                setExpandedTimesheetId((prev) =>
                                    prev === timesheet.id ? null : timesheet.id ?? null
                                )
                            }
                            hasPendingKm={hasPendingKm}
                        />
                    ))
                )}
            </div>

           <MobileToast ref={toast} />
        </div>
    );
};

export default Timesheets;